import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { NgxSpinnerModule } from 'ngx-spinner';
import { ToastModule } from 'primeng/toast';
import { distinctUntilChanged, filter, map } from 'rxjs';
import { environment } from 'src/environments/environment';

import { EventDetail } from './module/qrcode/models/qrcode';
import { AuthService } from './module/core/services/auth/auth.service';
import { EventService } from './module/core/services/event/event.service';
import { APP_TOAST_KEY } from './module/core/services/toastr/toastr.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, NgxSpinnerModule, ToastModule],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  readonly toastKey = APP_TOAST_KEY;

  private readonly router = inject(Router);
  private readonly eventsService = inject(EventService);
  private readonly authService = inject(AuthService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly frontendUrl = environment.FrontendUrl;

  loaded = false;
  error = false;
  /** Layout full-bleach na leitura de QR — mais altura/largura úteis para a câmera */
  scannerReadLayout = false;
  /** Login / acesso restrito: cabe na viewport sem rolagem da página */
  authLayout = false;

  ngOnInit(): void {
    this.syncRouteLayouts(this.router.url);

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map((event) => event.urlAfterRedirects),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((url) => {
        this.syncRouteLayouts(url);
        this.stripEventIdFromQrcodeUrlIfNeeded(url);
        const routeEventId = this.getRouteEventIdFromUrl(url);
        this.getDetailEvent(false, routeEventId, url);
      });

    if (this.router.navigated) {
      queueMicrotask(() => {
        this.syncRouteLayouts(this.router.url);
        this.stripEventIdFromQrcodeUrlIfNeeded(this.router.url);
        this.getDetailEvent(false, this.getRouteEventId(), this.router.url);
      });
    }
  }

  private syncRouteLayouts(url: string): void {
    const nextScanner =
      url.includes('/qrcode/read') ||
      url.includes('/qrcode/result') ||
      url.includes('/qrcode/failure') ||
      url.includes('/qrcode/documents');
    const nextAuth = url.includes('/auth');

    if (this.scannerReadLayout === nextScanner && this.authLayout === nextAuth) {
      return;
    }

    this.scannerReadLayout = nextScanner;
    this.authLayout = nextAuth;
    this.cdr.markForCheck();
  }

  /**
   * Carrega detalhe do evento para o shell (layout / estado).
   * @param navigateToReadAfterFetch — só `true` em fluxos explícitos (ex.: pós-login) que devem ir ao leitor; nunca a partir de `NavigationEnd`, senão sobrescreve rotas como `/qrcode/result`.
   * @param navigationUrl — URL após redirects (ex.: `NavigationEnd#urlAfterRedirects`) para não reprocessar em loop na página de erro.
   */
  getDetailEvent(navigateToReadAfterFetch = false, routeEventId?: string, navigationUrl?: string): void {
    const activeUrl = navigationUrl ?? this.router.url;

    if (this.isQrcodeErrorRoute(activeUrl)) {
      this.patchViewState(() => {
        this.loaded = true;
        this.error = false;
      });
      return;
    }

    this.patchViewState(() => {
      this.loaded = false;
      this.error = false;
    });

    if (routeEventId) {
      const stored = this.eventsService.getEventDetail();
      const storedId = stored?.id != null ? String(stored.id) : undefined;
      const routeId = String(routeEventId);
      if (storedId !== routeId) {
        this.resetSession();
      }
    }

    const storedEvent = this.eventsService.getEventDetail();
    const eventId = routeEventId ?? storedEvent?.id;

    if (!eventId) {
      this.patchViewState(() => {
        this.loaded = true;
        this.error = true;
      });
      console.error('eventId não está definido.');
      if (!this.isQrcodeErrorRoute(activeUrl)) {
        void this.router.navigate(['/qrcode/error'], { replaceUrl: true });
      }
      return;
    }

    if (!routeEventId && storedEvent?.id === eventId && storedEvent.name) {
      this.patchViewState(() => {
        this.loaded = true;
      });

      if (navigateToReadAfterFetch) {
        void this.router.navigate(['/qrcode/read']);
      }

      return;
    }

    this.eventsService.getEventById(eventId).subscribe({
      next: (res: EventDetail) => {
        this.patchViewState(() => {
          this.loaded = true;
          this.eventsService.setEventDetail(res);
        });

        if (navigateToReadAfterFetch) {
          void this.router.navigate(['/qrcode/read']);
        }
      },
      error: () => {
        this.patchViewState(() => {
          this.loaded = true;
          this.eventsService.setEventDetail({ id: eventId });
          this.error = false;
        });
      },
    });
  }

  private resetSession(): void {
    this.authService.removeToken();
    this.eventsService.removeEventDetail();
  }

  private patchViewState(update: () => void): void {
    queueMicrotask(() => {
      update();
      this.cdr.markForCheck();
    });
  }

  private getRouteEventId(): string | undefined {
    return this.getRouteEventIdFromUrl(this.router.url);
  }

  /**
   * O `eventId` na query só entra no fluxo de autenticação (`/auth?eventId=...`).
   * Nas rotas `/qrcode/*` o evento fica só no armazenamento (pós-login), sem expor o ID na barra.
   */
  private getRouteEventIdFromUrl(url: string): string | undefined {
    const path = url.split('?')[0] ?? url;
    if (!path.includes('/auth')) {
      return undefined;
    }
    const queryParams = this.router.parseUrl(url).queryParams;
    const id = queryParams['eventId'];
    return typeof id === 'string' && id.length > 0 ? id : undefined;
  }

  /** Remove `eventId` de URLs antigas/compartilhadas em `/qrcode/*` para alinhar à política de não expor o ID fora do login. */
  private stripEventIdFromQrcodeUrlIfNeeded(url: string): void {
    const pathAndQuery = url.split('#')[0] ?? url;
    const qIndex = pathAndQuery.indexOf('?');
    const path = qIndex >= 0 ? pathAndQuery.slice(0, qIndex) : pathAndQuery;
    if (!path.includes('/qrcode')) {
      return;
    }
    if (qIndex < 0) {
      return;
    }
    const search = pathAndQuery.slice(qIndex + 1);
    const params = new URLSearchParams(search);
    if (!params.has('eventId')) {
      return;
    }
    params.delete('eventId');
    const nextSearch = params.toString();
    const nextUrl = nextSearch ? `${path}?${nextSearch}` : path;
    void this.router.navigateByUrl(nextUrl, { replaceUrl: true });
  }

  private isQrcodeErrorRoute(url: string): boolean {
    const path = url.split('?')[0] ?? url;
    return path.endsWith('/qrcode/error') || path.includes('/qrcode/error/');
  }
}
